import { FC, RefObject, useCallback, useEffect, useRef } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Ratio } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { StepPrizesFormData } from "../../helpers/types";

import { useTranslate } from "@/common/hooks/useTranslate";
import { ImageUploader } from "@/components/ImageUploader/ImageUploader";
import { ImageUploadContext, ImageUploaderHandle } from "@/components/ImageUploader/types";
import { zeffyCardClasses } from "@/dashboard-design-system/Card/types";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Cross } from "@/icons/outlined";

const IMAGE_SIZE = 184;

interface PrizeImageFieldProps {
  index: number;
  prizeId: string;
  onUploaderChange?: (prizeId: string, uploaderRef: RefObject<ImageUploaderHandle | null> | null) => void;
}

export const PrizeImageField: FC<PrizeImageFieldProps> = ({ index, prizeId, onUploaderChange }) => {
  const { t } = useTranslate();
  const form = useFormContext<StepPrizesFormData>();
  const photoUrl = useWatch({ control: form.control, name: `prizes.${index}.photoUrl` });
  const uploaderRef = useRef<ImageUploaderHandle>(null);

  // Registers the ref, not its value: the uploader handle is recreated on every render and a snapshot would finalize nothing.
  useEffect(() => {
    onUploaderChange?.(prizeId, uploaderRef);
    return () => onUploaderChange?.(prizeId, null);
  }, [onUploaderChange, prizeId]);

  const deleteImage = useCallback(() => {
    form.setValue(`prizes.${index}.photoUrl`, null, { shouldDirty: true });
    uploaderRef.current?.setImage(null);
  }, [form, index]);

  return (
    <Stack spacing={0.5} sx={{ flexShrink: 0 }}>
      <DashboardTypography variant="body2">{t("dashboard", "ticketing.imageLabel")}</DashboardTypography>
      <Box
        sx={{
          width: IMAGE_SIZE,
          height: IMAGE_SIZE,
          position: "relative",
          [`& .${zeffyCardClasses.content}`]: { padding: 0 },
        }}
      >
        <ImageUploader
          ref={uploaderRef}
          data-test="prize-image-input"
          field={{ form, name: `prizes.${index}.photoUrl`, isUploadingName: "isImageUploading" }}
          editor={{ kind: "square", targetSize: 800, centerFit: true }}
          layout={{ ratio: Ratio.Square }}
          upload={{
            context: ImageUploadContext.LiveFormEditorRateImage,
            preset: "square_800",
            persistToAssetLibrary: false,
          }}
          labels={{ import: t("dashboard", "ticketing.uploadImage") }}
          emptyState={{ actions: [{ type: "upload", label: t("dashboard", "ticketing.uploadImage") }] }}
          showDeleteButton={false}
          onBeforeDelete={() => {
            deleteImage();
            return false;
          }}
        />
        {photoUrl && (
          <DashboardIconButton
            onClick={deleteImage}
            vibe="neutral"
            size="small"
            data-test="prize-image-delete"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.95)" },
            }}
          >
            <Cross />
          </DashboardIconButton>
        )}
      </Box>
    </Stack>
  );
};
